"use client";

import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/lib/utils";
import Image from "next/image";
import Link from "next/link";
import { memo } from "react";
import { servicesHeroMap } from "./constants";
import { ServicesHeroProps } from "./types";

const ServicesHero = ({
  className,
  variant = "standard",
  ...rest
}: ServicesHeroProps) => {
  const {
    title,
    description,
    primaryButton,
    secondaryButton,
    imageUrl,
    imageDimensions,
  } = servicesHeroMap[variant];
  return (
    // 2026-05-25 — added the Figma-spec hero gradient on lg+ as the
    // default (was: solid bg-background-hero at every breakpoint).
    // After Imran A/B'd the home hero treatment against the Standard
    // page using the verbatim Figma fill (0% #F7F7F9 → 100% #EBF0FB,
    // linear top→bottom), this set of stops won. Centralizing it
    // here so every shared services hero (Standard, Urgent, Certified,
    // Notarized, Enterprise, Quality) picks it up automatically and
    // can't drift again. Mobile/tablet still fall back to the solid
    // bg-background-hero color — gradient is desktop-only this pass.
    // bg-background-hero (background-color) and the gradient utils
    // (background-image) coexist by design; on lg+ the gradient
    // paints over the color. If a specific page needs a different
    // hero treatment, override via the className prop as before —
    // the override lands AFTER the defaults in cn(), so it wins.
    <section
      className={cn(
        "overflow-hidden section pt-[var(--main-header-height,56px)] lg:pt-[var(--main-header-height,54px)] bg-background-hero lg:bg-gradient-to-b lg:from-[#F7F7F9] lg:to-[#EBF0FB]",
        className
      )}
      {...rest}
    >
      <div className="container mx-auto">
        <div className="grid grid-cols-1 pt-6 pb-6 gap-8 md:pt-30 lg:text-start lg:grid-cols-[9fr_11fr] lg:gap-[22px] lg:py-20">
          <div className="flex flex-col lg:justify-center gap-8 lg:max-w-[496px]">
            <div className="grid grid-cols-1 gap-2 md:gap-8">
              <Typography variant="headingMain" className="w-full">
                <h1>{title}</h1>
              </Typography>
              <Typography variant="bodyLg" asChild>
                <p>{description}</p>
              </Typography>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:flex flex-wrap lg:gap-2.5">
              {!!primaryButton && (
                <Button
                  asChild
                  variant="primary"
                  className="sm:w-[247px] h-[48px]"
                >
                  <Link href={primaryButton.href} className="!text-base w-full">
                    {primaryButton.label}
                  </Link>
                </Button>
              )}
              {!!secondaryButton && (
                <Button
                  asChild
                  variant="primary-outline"
                  className="h-[48px] sm:w-[141px]"
                >
                  <Link
                    href={secondaryButton?.href}
                    className="!text-base w-full"
                  >
                    {secondaryButton?.label}
                  </Link>
                </Button>
              )}
            </div>
          </div>
          {imageUrl && (
            <div className="flex items-start">
              <Image
                alt="Languex Illustration"
                src={imageUrl}
                width={imageDimensions?.width}
                height={imageDimensions?.height}
                className="flex-auto min-w-0"
                quality={100}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default memo(ServicesHero);
